import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { invoicesApi } from '../services/api';
import { StatusBadge } from '../components/shared/StatusBadge';
import { Spinner } from '../components/shared/Spinner';
import { DeleteModal } from '../components/invoices/DeleteModal';
import { UploadModal } from '../components/invoices/UploadModal';
import { useToast } from '../context/ToastContext';
import { summarize, computeDuplicateGroups } from '../lib/invoice-summary';
import type { DropboxArchiveStatus, InvoiceStatus } from '../types';

const STATUS_FILTERS: Array<{ key: InvoiceStatus | 'all'; label: string }> = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'extracted', label: 'Extraites' },
  { key: 'validated', label: 'Validées' },
  { key: 'created', label: 'Créées Zoho' },
  { key: 'error', label: 'En erreur' },
  { key: 'rejected', label: 'Rejetées' },
];

const VALID_FILTERS = new Set(STATUS_FILTERS.map((f) => f.key));

// Libellés lisibles de la source d'une facture (affichés dans la liste).
const SOURCE_LABELS: Record<string, string> = {
  gmail: 'Email',
  dropbox: 'Dropbox',
  dropbox_ndf_dominique: 'NDF Dominique',
  dropbox_ndf_fabien: 'NDF Fabien',
  manual: 'Upload manuel',
  manual_upload: 'Upload manuel',
};

function SourceBadge({ source }: { source: string }) {
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200">
      {SOURCE_LABELS[source] ?? source}
    </span>
  );
}

type SortKey = 'created_at' | 'invoice_date' | 'vendor_name' | 'total_ht' | 'total_ttc';
type SortOrder = 'asc' | 'desc';

const VALID_SORTS: SortKey[] = [
  'created_at',
  'invoice_date',
  'vendor_name',
  'total_ht',
  'total_ttc',
];

export function InvoicesPage() {
  // ─── URL state (status / sort / order / search) ─────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatus = searchParams.get('status') ?? 'all';
  const filter = (VALID_FILTERS.has(rawStatus as InvoiceStatus | 'all')
    ? rawStatus
    : 'all') as InvoiceStatus | 'all';

  const sort: SortKey = (VALID_SORTS as readonly string[]).includes(searchParams.get('sort') ?? '')
    ? (searchParams.get('sort') as SortKey)
    : 'created_at';
  const order: SortOrder = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const search = searchParams.get('search') ?? '';

  const updateParam = (key: string, value: string | null) => {
    if (value == null || value === '' || value === 'all') {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
    setSearchParams(searchParams, { replace: true });
  };
  // Vue spéciale « archivage échoué » : dimension orthogonale au statut, gérée
  // via le param ?archive=failed. Mutuellement exclusive avec le filtre statut.
  const archiveView = searchParams.get('archive') === 'failed';

  const setFilter = (key: InvoiceStatus | 'all') => {
    searchParams.delete('archive');
    updateParam('status', key);
    setSelected(new Set());
  };
  const setArchiveView = (on: boolean) => {
    if (on) {
      searchParams.delete('status');
      searchParams.set('archive', 'failed');
    } else {
      searchParams.delete('archive');
    }
    setSearchParams(searchParams, { replace: true });
    setSelected(new Set());
  };
  const setSearch = (v: string) => updateParam('search', v);
  const toggleSort = (key: SortKey) => {
    if (sort === key) {
      updateParam('order', order === 'asc' ? 'desc' : 'asc');
    } else {
      updateParam('sort', key);
      updateParam('order', 'desc');
    }
  };

  // ─── Data ───────────────────────────────────────────────────────────────
  const q = useQuery({
    queryKey: ['invoices', { status: filter }],
    queryFn: () =>
      invoicesApi.list({
        status: filter === 'all' ? undefined : filter,
        limit: 100,
      }),
    enabled: !archiveView,
  });

  // Always-on : les échecs d'archivage ne concernent que les factures créées
  // (la bill Zoho existe, seul l'upload Dropbox a échoué). Sert le compteur de
  // la bannière globale ET la vue « archivage échoué ».
  const createdQ = useQuery({
    queryKey: ['invoices', { status: 'created' as InvoiceStatus }],
    queryFn: () => invoicesApi.list({ status: 'created', limit: 100 }),
  });
  const archiveFailures = useMemo(
    () =>
      (createdQ.data?.invoices ?? []).filter(
        (i) => i.dropbox_archive_status === 'failed' || i.dropbox_archive_status === 'partial',
      ),
    [createdQ.data],
  );

  // Source de la liste affichée : la vue « archivage échoué » remplace la liste
  // par les factures en échec ; sinon, la liste filtrée par statut.
  const activeQ = archiveView ? createdQ : q;
  const baseInvoices = archiveView ? archiveFailures : (q.data?.invoices ?? []);

  // Résumés enrichis (tri + recherche côté client, limite 100 invoices).
  const summaries = useMemo(() => baseInvoices.map(summarize), [baseInvoices]);
  // Doublons potentiels : même fournisseur + même n° de facture (calculé sur
  // l'ensemble chargé, pas seulement le filtre courant — mais q.data dépend
  // du filtre. Acceptable : on détecte les doublons visibles dans le filtre).
  const dupGroups = useMemo(() => computeDuplicateGroups(summaries), [summaries]);

  const filtered = useMemo(() => {
    if (!search.trim()) return summaries;
    const needle = search.trim().toLowerCase();
    return summaries.filter((s) =>
      [s.filename, s.vendor_name, s.invoice_number, s.source_reference]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle)),
    );
  }, [summaries, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      // null en queue de tri.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return order === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv), 'fr', { sensitivity: 'base' });
      return order === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort, order]);

  // ─── Sélection multiple + upload ────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const navigate = useNavigate();

  const allVisibleIds = useMemo(() => sorted.map((s) => s.id), [sorted]);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allVisibleIds.some((id) => selected.has(id));

  const toggleAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(allVisibleIds)));
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ─── Mutation suppression masse ─────────────────────────────────────────
  const qc = useQueryClient();
  const toast = useToast();
  const bulkMutation = useMutation({
    mutationFn: (reason: string) => invoicesApi.bulkDelete([...selected], reason),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      setBulkDeleteOpen(false);
      setSelected(new Set());
      const skippedCreated = resp.skipped.filter((s) => s.reason === 'already_created_in_zoho').length;
      if (skippedCreated > 0) {
        toast.info(`${resp.deleted} supprimée(s), ${skippedCreated} ignorée(s) (déjà créées dans Zoho)`);
      } else {
        toast.success(`${resp.deleted} facture(s) supprimée(s)`);
      }
    },
    onError: (err) => toast.error(`Suppression échouée : ${String(err)}`),
  });

  // ─── Liens ──────────────────────────────────────────────────────────────
  const detailHref = (id: string) => {
    const qs = searchParams.toString();
    return `/factures/liste/${id}${qs ? `?return=${encodeURIComponent(qs)}` : ''}`;
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Factures</h1>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5"
          >
            <span aria-hidden>＋</span> Ajouter des factures
          </button>
          <span className="text-sm text-slate-500">{sorted.length} résultat(s)</span>
        </div>
      </div>

      {/* Bannière globale : factures non archivées dans Dropbox. */}
      {archiveFailures.length > 0 && !archiveView && (
        <button
          type="button"
          onClick={() => setArchiveView(true)}
          className="w-full text-left rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 hover:bg-red-100 transition-colors flex items-center gap-2"
        >
          <span aria-hidden className="text-base">⚠️</span>
          <span className="flex-1">
            <strong>{archiveFailures.length}</strong> facture{archiveFailures.length > 1 ? 's' : ''} n
            {archiveFailures.length > 1 ? "'ont" : "'a"} pas été archivée
            {archiveFailures.length > 1 ? 's' : ''} dans Dropbox.
          </span>
          <span className="font-medium underline whitespace-nowrap">Afficher →</span>
        </button>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                filter === f.key && !archiveView
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
          {(archiveFailures.length > 0 || archiveView) && (
            <button
              type="button"
              onClick={() => setArchiveView(!archiveView)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                archiveView
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-red-700 ring-1 ring-red-200 hover:bg-red-50'
              }`}
            >
              ⚠️ Archivage échoué{archiveFailures.length > 0 ? ` (${archiveFailures.length})` : ''}
            </button>
          )}
        </div>

        <input
          type="search"
          placeholder="Rechercher (fichier, fournisseur, n°…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] max-w-xs rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-slate-700">
              <strong>{selected.size}</strong> sélectionnée{selected.size > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-2 py-1 rounded-md text-slate-600 hover:bg-slate-100"
            >
              Effacer
            </button>
            <button
              type="button"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkMutation.isPending}
              className="px-3 py-1.5 rounded-md font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {bulkMutation.isPending && <Spinner />}
              🗑️ Supprimer la sélection
            </button>
          </div>
        )}
      </div>

      {activeQ.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : activeQ.isError ? (
        <p className="text-red-600">Erreur : {String(activeQ.error)}</p>
      ) : (
        <>
          {/* Vue desktop : tableau */}
          <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                  <SortableTh sort={sort} order={order} k="invoice_date" onToggle={toggleSort}>Date facture</SortableTh>
                  <SortableTh sort={sort} order={order} k="vendor_name" onToggle={toggleSort}>Fournisseur</SortableTh>
                  <th className="px-4 py-2 font-medium">N° / Fichier</th>
                  <SortableTh sort={sort} order={order} k="total_ht" onToggle={toggleSort} numeric>HT</SortableTh>
                  <SortableTh sort={sort} order={order} k="total_ttc" onToggle={toggleSort} numeric>TTC</SortableTh>
                  <th className="px-4 py-2 font-medium">Statut</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sorted.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onChange={() => toggleOne(s.id)}
                        aria-label={`Sélectionner ${s.filename}`}
                      />
                    </td>
                    <td className="px-4 py-2 tabular-nums">{s.invoice_date ?? '-'}</td>
                    <td className="px-4 py-2 font-medium truncate max-w-[200px]" title={s.vendor_name ?? ''}>
                      {s.vendor_name ?? '-'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      <div className="font-mono text-xs">{s.invoice_number ?? '-'}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[200px]" title={s.filename}>
                        {s.filename}
                      </div>
                      <div className="mt-1">
                        <SourceBadge source={s.source} />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {s.total_ht != null ? `${s.total_ht.toFixed(2)} €` : '-'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {s.total_ttc != null ? `${s.total_ttc.toFixed(2)} €` : '-'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1 items-start">
                        <StatusBadge status={s.status} />
                        {dupGroups.has(s.id) && (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                            title={`Une facture similaire existe (même fournisseur + même n°). ${dupGroups.get(s.id)!.length} autre(s).`}
                          >
                            ⚠️ Doublon possible
                          </span>
                        )}
                        <ArchiveBadge
                          status={s.dropbox_archive_status}
                          error={s.dropbox_archive_error}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link to={detailHref(s.id)} className="text-blue-600 hover:underline whitespace-nowrap">
                        Ouvrir →
                      </Link>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400 italic">
                      Aucune facture pour ce filtre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Vue mobile : cartes */}
          <div className="md:hidden space-y-2">
            {sorted.map((s) => (
              <Link
                key={s.id}
                to={detailHref(s.id)}
                className="block bg-white rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate flex-1">{s.vendor_name ?? s.filename}</span>
                  <StatusBadge status={s.status} />
                </div>
                {dupGroups.has(s.id) && (
                  <div className="mt-1">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 ring-1 ring-amber-300">
                      ⚠️ Doublon possible
                    </span>
                  </div>
                )}
                {(s.dropbox_archive_status === 'failed' || s.dropbox_archive_status === 'partial') && (
                  <div className="mt-1">
                    <ArchiveBadge
                      status={s.dropbox_archive_status}
                      error={s.dropbox_archive_error}
                    />
                  </div>
                )}
                <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                  {s.invoice_date && <span>📅 {s.invoice_date}</span>}
                  {s.invoice_number && <span>· N° {s.invoice_number}</span>}
                  <SourceBadge source={s.source} />
                </div>
                {s.total_ttc != null && (
                  <div className="mt-1 text-sm tabular-nums">
                    <strong>{s.total_ttc.toFixed(2)} €</strong>
                    {s.total_ht != null && (
                      <span className="text-slate-500"> (HT {s.total_ht.toFixed(2)})</span>
                    )}
                  </div>
                )}
              </Link>
            ))}
            {sorted.length === 0 && (
              <p className="text-center text-slate-400 italic py-8">Aucune facture pour ce filtre.</p>
            )}
          </div>
        </>
      )}

      <DeleteModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={(reason) => bulkMutation.mutate(reason)}
        busy={bulkMutation.isPending}
      />

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        // Upload en masse : la modale gère son propre récap par fichier et reste
        // sur la liste. À chaque succès elle invalide la liste (peuplement en
        // direct) ; le toast résumé est émis par la modale en fin de lot.
        onChanged={() => qc.invalidateQueries({ queryKey: ['invoices'] })}
      />
    </div>
  );
}

function ArchiveBadge({
  status,
  error,
}: {
  status: DropboxArchiveStatus;
  error: string | null;
}) {
  if (status !== 'failed' && status !== 'partial') return null;
  const label = status === 'partial' ? 'Archivage partiel' : 'Archivage échoué';
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 ring-1 ring-red-300"
      title={error ?? 'Archivage Dropbox échoué'}
    >
      ⚠️ {label}
    </span>
  );
}

function SortableTh({
  children,
  sort,
  order,
  k,
  onToggle,
  numeric,
}: {
  children: React.ReactNode;
  sort: SortKey;
  order: SortOrder;
  k: SortKey;
  onToggle: (k: SortKey) => void;
  numeric?: boolean;
}) {
  const active = sort === k;
  return (
    <th
      className={`px-4 py-2 font-medium select-none cursor-pointer hover:text-slate-900 ${numeric ? 'text-right' : ''}`}
      onClick={() => onToggle(k)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <span className="text-xs">{active ? (order === 'asc' ? '▲' : '▼') : '↕'}</span>
      </span>
    </th>
  );
}
